/**
 * DashboardTower - 指挥塔风格 Dashboard 内容区
 *
 * 设计理念: 将 Dashboard 内容区升级为"企业经营指挥塔"
 * - Insight Strip 六大指标横向条
 * - Filter Pills 筛选 + 搜索 + 视图切换
 * - 三栏布局: 项目列表 | 增项动态 | 整改动态
 *
 * 重要: 本组件仅替换 Dashboard 的内容区。
 * Sidebar 和 HeaderBar 属于 MainLayout，不在本组件范围内。
 * 背景设为 transparent，继承 MainLayout 的渐变底色。
 *
 * 集成方式: 作为 Dashboard/index.tsx 中的第五个视图选项
 */
import React, { useState, useMemo } from 'react';
import type { ProjectOverviewData } from '../../services/dashboardService';
import type { DashboardFocusItem } from '../../services/statisticsService';

// ============== CSS TOKENS — 与 themeConfig.ts 完全一致 ==============
const tokens: Record<string, string> = {
  '--bg-card': 'rgba(224, 230, 234, 0.82)',
  '--bg-card-hover': 'rgba(214, 221, 226, 0.92)',
  '--bg-card-elevated': 'rgba(223, 229, 233, 0.92)',
  '--bg-surface': 'rgba(210, 218, 224, 0.9)',
  '--bg-elevated': 'rgba(232, 237, 240, 0.98)',
  '--bg-control': 'rgba(236, 240, 243, 0.88)',
  '--bg-control-strong': 'rgba(228, 233, 237, 0.94)',
  '--text-primary': '#10171d',
  '--text-secondary': '#455763',
  '--text-muted': '#667986',
  '--border': 'rgba(16, 27, 35, 0.08)',
  '--border-hover': 'rgba(16, 27, 35, 0.16)',
  '--accent-gold': '#b99258',
  '--accent-gold-soft': 'rgba(185, 146, 88, 0.14)',
  '--accent-blue': '#547590',
  '--accent-blue-soft': 'rgba(84, 117, 144, 0.12)',
  '--accent-mint': '#5d8a7f',
  '--accent-mint-soft': 'rgba(101, 214, 184, 0.16)',
  '--accent-red': '#c46d5f',
  '--accent-red-soft': 'rgba(196, 109, 95, 0.14)',
  '--accent-amber': '#d9a86b',
  '--accent-amber-soft': 'rgba(246, 196, 83, 0.16)',
  '--accent-purple': '#9b8dc9',
  '--accent-purple-soft': 'rgba(155, 141, 201, 0.16)',
  '--shadow': '0 28px 60px rgba(46, 60, 70, 0.14)',
  '--shadow-sm': '0 8px 24px rgba(46, 60, 70, 0.1)',
  '--radius-lg': '16px',
  '--radius-md': '12px',
  '--radius-sm': '8px',
};

// ============== TYPES ==============
type ViewMode = 'tower' | 'compact' | 'table' | 'list';
type FilterStatus = 'all' | 'active' | 'completed' | 'overdue' | 'risky';

interface DashboardTowerProps {
  projects: ProjectOverviewData[];
  dashboardFocusData?: DashboardFocusItem[];
  additions?: DashboardFocusItem[];
  rectifications?: DashboardFocusItem[];
  loading?: boolean;
  onProjectClick?: (project: ProjectOverviewData) => void;
  onAdditionClick?: (item: DashboardFocusItem) => void;
  onRectificationClick?: (item: DashboardFocusItem) => void;
  onAddProject?: () => void;
  onAddAddition?: () => void;
  onAddRectification?: () => void;
  onExport?: () => void;
  onRefresh?: () => void;
  summaryMetrics?: {
    totalProjects: number;
    activeProjects: number;
    totalBudget?: number;
    totalActualCost: number;
    totalBudgetUsage?: number;
    overBudgetCount?: number;
    overdueCount?: number;
    staleCount?: number;
    riskyCount?: number;
    pendingApprovalCount?: number;
  };
}

// ============== UTILITIES ==============
const fmt = (n?: number | null, compact = false): string => {
  if (n == null || isNaN(n)) return '--';
  if (compact && Math.abs(n) >= 10000) return `¥${(n / 10000).toFixed(1)}万`;
  return `¥${n.toLocaleString('zh-CN', { maximumFractionDigits: 0 })}`;
};

const toFinite = (v: unknown, fallback = 0): number => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return fallback;
};

const getExecutionColor = (pct: number): string => {
  if (pct === 0) return 'var(--text-muted)';
  if (pct < 50) return 'var(--accent-mint)';
  if (pct < 70) return 'var(--accent-blue)';
  if (pct < 90) return 'var(--accent-amber)';
  return 'var(--accent-red)';
};

const STATUS_CONFIG: Record<string, { color: string; bg: string; border: string; dotColor: string }> = {
  '进行中': { color: 'var(--accent-gold)', bg: 'rgba(185, 146, 88, 0.14)', border: 'rgba(185, 146, 88, 0.3)', dotColor: 'var(--accent-gold)' },
  '已完成': { color: 'var(--accent-mint)', bg: 'rgba(101, 214, 184, 0.16)', border: 'rgba(101, 214, 184, 0.3)', dotColor: 'var(--accent-mint)' },
  '待开始': { color: 'var(--text-muted)', bg: 'rgba(92, 107, 120, 0.12)', border: 'rgba(92, 107, 120, 0.2)', dotColor: 'var(--text-muted)' },
  '已暂停': { color: 'var(--accent-amber)', bg: 'rgba(246, 196, 83, 0.16)', border: 'rgba(246, 196, 83, 0.3)', dotColor: 'var(--accent-amber)' },
};

// ============== HERO METRIC CARD ==============
function HeroMetricCard({ metric }: { metric: { label: string; value: string; detail: string; color?: string; bg?: string; icon: React.ReactNode } }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 16px',
        background: 'var(--bg-card)',
        border: `1px solid ${hovered ? 'var(--border-hover)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-md)',
        minWidth: 165, flexShrink: 0,
        cursor: 'pointer', transition: 'all 0.2s',
        transform: hovered ? 'translateY(-1px)' : '',
        boxShadow: hovered ? 'var(--shadow-sm)' : 'none',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{
        width: 38, height: 38, borderRadius: 9, flexShrink: 0,
        background: metric.bg || 'var(--accent-gold-soft)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17,
      }}>
        {metric.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 500, marginBottom: 2 }}>{metric.label}</div>
        <div style={{
          fontSize: 19, fontWeight: 700,
          fontFamily: '"JetBrains Mono", monospace',
          color: metric.color || 'var(--text-primary)', lineHeight: 1,
        }}>
          {metric.value}
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{metric.detail}</div>
      </div>
    </div>
  );
}

// ============== PROJECT CARD ==============
function ProjectCard({ project, onClick }: { project: ProjectOverviewData; onClick?: (p: ProjectOverviewData) => void }) {
  const [hovered, setHovered] = useState(false);

  const cfg = STATUS_CONFIG[project.status] || STATUS_CONFIG['进行中'];
  const budget = toFinite(project.budget);
  const actualCost = toFinite(project.actualCost);
  const budgetUsage = budget > 0 ? (actualCost / budget) * 100 : 0;
  const progress = toFinite(project.progress);
  const execColor = getExecutionColor(budgetUsage);
  const hasBudget = budget > 0;
  const daysRemaining = project.daysRemaining;
  const isOverdue = toFinite(daysRemaining) < 0;
  const isCompleted = project.status === '已完成';
  const projectCode = (project as any).projectCode || 'UNSET';

  return (
    <div
      style={{
        background: 'var(--bg-card)',
        backdropFilter: 'blur(8px)',
        border: `1px solid ${hovered ? 'var(--border-hover)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden', cursor: 'pointer',
        transition: 'all 0.2s',
        transform: hovered ? 'translateY(-2px)' : '',
        boxShadow: hovered ? 'var(--shadow)' : 'var(--shadow-sm)',
        position: 'relative',
      }}
      onClick={() => onClick?.(project)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ height: 3, background: cfg.dotColor }} />
      <div style={{ padding: '16px 16px 12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 7 }}>
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.12em',
            color: 'var(--accent-blue)', background: 'var(--accent-blue-soft)',
            padding: '2px 7px', borderRadius: 5,
            fontFamily: '"JetBrains Mono", monospace',
          }}>
            {projectCode.toUpperCase()}
          </span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 600,
            color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
          }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.dotColor }} />
            {project.status}
          </span>
        </div>

        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em', lineHeight: 1.3, marginBottom: 4 }}>
          {project.name}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          V{project.budgetVersion || 1}预算 · {isCompleted ? '已竣工' : project.scheduleStatusLabel || '进行中'} · 更新: {project.recentActivity || '暂无'}
        </div>
      </div>

      <div style={{ padding: '0 16px 12px' }}>
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
            <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 500 }}>预算执行</span>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: '"JetBrains Mono", monospace' }}>
              {hasBudget ? `${fmt(actualCost, true)} / ${fmt(budget, true)}` : '未配置'}
            </span>
          </div>
          <div style={{ height: 5, background: 'var(--bg-surface)', borderRadius: 3, overflow: 'hidden' }}>
            {!hasBudget ? (
              <div style={{ height: '100%', border: '1.5px dashed var(--text-muted)', borderRadius: 3, opacity: 0.3 }} />
            ) : (
              <div style={{
                height: '100%', width: `${Math.min(budgetUsage, 100)}%`,
                background: execColor, borderRadius: 3,
                transition: 'width 1s ease-out',
                boxShadow: budgetUsage > 100 ? `0 0 8px ${execColor}` : undefined,
              }} />
            )}
          </div>
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1,
          background: 'var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', marginBottom: 10,
        }}>
          {[
            { label: '累计收入', value: fmt(project.revenueTotal || 0, true) },
            { label: '预估利润', value: isCompleted ? '¥0' : fmt((project.revenueTotal || 0) - actualCost, true), color: (project.revenueTotal || 0) - actualCost < 0 ? 'var(--accent-red)' : undefined },
            { label: '执行率', value: hasBudget ? `${budgetUsage.toFixed(1)}%` : '—', color: hasBudget ? execColor : undefined },
          ].map((cell, i) => (
            <div key={i} style={{ background: 'var(--bg-card)', padding: '8px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 700, fontFamily: '"JetBrains Mono", monospace', color: cell.color || 'var(--text-primary)', lineHeight: 1, marginBottom: 2 }}>
                {cell.value}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{cell.label}</div>
            </div>
          ))}
        </div>

        {(project.riskTags?.length > 0 || !hasBudget) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
            {!hasBudget && (
              <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: 'var(--accent-red-soft)', color: 'var(--accent-red)' }}>
                预算未配置
              </span>
            )}
            {project.riskTags?.filter(t => t !== '长期无更新').slice(0, 2).map((tag, i) => (
              <span key={i} style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: 'var(--accent-amber-soft)', color: 'var(--accent-amber)' }}>
                {tag}
              </span>
            ))}
            {isOverdue && (
              <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: 'var(--accent-red-soft)', color: 'var(--accent-red)' }}>
                超期 {Math.abs(toFinite(daysRemaining))} 天
              </span>
            )}
          </div>
        )}

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{isCompleted ? '结算进度' : '工程进度'}</span>
            <span style={{ fontSize: 12, fontWeight: 700, fontFamily: '"JetBrains Mono", monospace', color: isOverdue && !isCompleted ? 'var(--accent-red)' : 'var(--accent-gold)' }}>
              {progress.toFixed(0)}%
            </span>
          </div>
          <div style={{ height: 6, background: 'var(--bg-surface)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${Math.min(progress, 100)}%`,
              background: isOverdue && !isCompleted
                ? 'linear-gradient(90deg, var(--accent-red) 0%, #d96b6b 100%)'
                : 'linear-gradient(90deg, var(--accent-gold) 0%, #d9b87a 100%)',
              borderRadius: 3, transition: 'width 1s ease-out',
            }} />
          </div>
        </div>
      </div>

      <div style={{
        padding: '10px 16px', borderTop: '1px solid var(--border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: 'rgba(0,0,0,0.02)',
      }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {['⏱', '📦', '→'].map((icon, i) => (
            <button
              key={i}
              onClick={e => { e.stopPropagation(); }}
              style={{
                padding: '4px 9px', background: 'var(--bg-surface)', border: '1px solid var(--border)',
                borderRadius: 6, fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer',
                transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 3,
              }}
            >
              {icon}
            </button>
          ))}
        </div>
        <span style={{ fontSize: 11, color: isOverdue && !isCompleted ? 'var(--accent-red)' : 'var(--text-muted)' }}>
          {isCompleted ? '已竣工' : toFinite(daysRemaining) >= 0 ? `剩余 ${toFinite(daysRemaining)} 天` : `已超期 ${Math.abs(toFinite(daysRemaining))} 天`}
        </span>
      </div>
    </div>
  );
}

// ============== ADDITION COLUMN ==============
const ADDITION_STATUS_CONFIG: Record<string, { color: string; bg: string; icon: string; label: string }> = {
  '待审批': { color: 'var(--accent-amber)', bg: 'var(--accent-amber-soft)', icon: '⏳', label: '待审批' },
  '审批中': { color: 'var(--accent-amber)', bg: 'var(--accent-amber-soft)', icon: '⏳', label: '审批中' },
  '已批准': { color: 'var(--accent-mint)', bg: 'var(--accent-mint-soft)', icon: '\u2713', label: '已批准' },
  '已结算': { color: 'var(--accent-mint)', bg: 'var(--accent-mint-soft)', icon: '\u2713', label: '已结算' },
  '已驳回': { color: 'var(--accent-red)', bg: 'var(--accent-red-soft)', icon: '\u2715', label: '已驳回' },
};

function AdditionStreamItem({
  item, onClick,
}: {
  item: DashboardFocusItem;
  onClick?: (item: DashboardFocusItem) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const statusCfg = ADDITION_STATUS_CONFIG[item.status] || { color: 'var(--text-muted)', bg: 'var(--bg-surface)', icon: '📋', label: item.status };
  const projectCode = (item as any).projectCode || item.projectId.slice(-6).toUpperCase();

  return (
    <div
      style={{
        display: 'flex', gap: 10, padding: 10,
        background: hovered ? 'var(--bg-card-hover)' : 'var(--bg-card)',
        border: `1px solid ${hovered ? 'var(--border-hover)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-md)', marginBottom: 6,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.18s',
        transform: hovered ? 'translateX(2px)' : '',
        boxShadow: hovered ? 'var(--shadow-sm)' : 'none',
      }}
      onClick={() => onClick?.(item)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
        background: statusCfg.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14,
      }}>
        {statusCfg.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 10, fontWeight: 600, color: 'var(--accent-blue)',
          fontFamily: '"JetBrains Mono", monospace',
          marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {projectCode} · {item.projectName}
        </div>
        <div style={{
          fontSize: 12, fontWeight: 600, color: 'var(--text-primary)',
          lineHeight: 1.3, marginBottom: 3,
          overflow: 'hidden', display: '-webkit-box',
          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        }}>
          {item.title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 4 }}>
          <span style={{
            fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 999,
            background: statusCfg.bg, color: statusCfg.color,
          }}>
            {statusCfg.label}
          </span>
          {item.primaryTag && item.primaryTag !== item.status && (
            <span style={{
              fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 999,
              background: 'var(--accent-blue-soft)', color: 'var(--accent-blue)',
            }}>
              {item.primaryTag}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{item.timeLabel}</span>
          <span style={{
            fontSize: 12, color: hovered ? 'var(--accent-gold)' : 'var(--text-muted)',
            transition: 'all 0.15s',
            transform: hovered ? 'translateX(2px)' : '',
          }}>
          {'>'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ============== RECTIFICATION COLUMN ==============
const RECTIFICATION_STATUS_CONFIG: Record<string, { color: string; bg: string; icon: string; label: string }> = {
  '待处理': { color: 'var(--accent-amber)', bg: 'var(--accent-amber-soft)', icon: '⏳', label: '待处理' },
  '进行中': { color: 'var(--accent-amber)', bg: 'var(--accent-amber-soft)', icon: '⏳', label: '进行中' },
  '已验收': { color: 'var(--accent-mint)', bg: 'var(--accent-mint-soft)', icon: '\u2713', label: '已验收' },
  '已驳回': { color: 'var(--accent-red)', bg: 'var(--accent-red-soft)', icon: '\u2715', label: '已驳回' },
  '紧急': { color: 'var(--accent-red)', bg: 'var(--accent-red-soft)', icon: '🚨', label: '紧急' },
  '高风险': { color: 'var(--accent-red)', bg: 'var(--accent-red-soft)', icon: '\u26A0\uFE0F', label: '高风险' },
};

function RectificationStreamItem({
  item, onClick,
}: {
  item: DashboardFocusItem;
  onClick?: (item: DashboardFocusItem) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const statusCfg = RECTIFICATION_STATUS_CONFIG[item.status] || { color: 'var(--text-muted)', bg: 'var(--bg-surface)', icon: '🔧', label: item.status };
  const projectCode = (item as any).projectCode || item.projectId.slice(-6).toUpperCase();

  return (
    <div
      style={{
        display: 'flex', gap: 10, padding: 10,
        background: hovered ? 'var(--bg-card-hover)' : 'var(--bg-card)',
        border: `1px solid ${hovered ? 'var(--border-hover)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-md)', marginBottom: 6,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.18s',
        transform: hovered ? 'translateX(2px)' : '',
        boxShadow: hovered ? 'var(--shadow-sm)' : 'none',
      }}
      onClick={() => onClick?.(item)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
        background: statusCfg.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14,
      }}>
        {statusCfg.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 10, fontWeight: 600, color: 'var(--accent-blue)',
          fontFamily: '"JetBrains Mono", monospace',
          marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {projectCode} · {item.projectName}
        </div>
        <div style={{
          fontSize: 12, fontWeight: 600, color: 'var(--text-primary)',
          lineHeight: 1.3, marginBottom: 3,
          overflow: 'hidden', display: '-webkit-box',
          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        }}>
          {item.title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 4 }}>
          <span style={{
            fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 999,
            background: statusCfg.bg, color: statusCfg.color,
          }}>
            {statusCfg.label}
          </span>
          {item.primaryTag && item.primaryTag !== item.status && (
            <span style={{
              fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 999,
              background: 'var(--accent-blue-soft)', color: 'var(--accent-blue)',
            }}>
              {item.primaryTag}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{item.timeLabel}</span>
          <span style={{
            fontSize: 12, color: hovered ? 'var(--accent-gold)' : 'var(--text-muted)',
            transition: 'all 0.15s',
            transform: hovered ? 'translateX(2px)' : '',
          }}>
          {'>'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ============== SUMMARY STAT CARD ==============
function StatCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{
      flex: 1, textAlign: 'center',
      padding: '8px 6px',
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)',
    }}>
      <div style={{
        fontSize: 16, fontWeight: 700,
        fontFamily: '"JetBrains Mono", monospace',
        color: color || 'var(--text-primary)', lineHeight: 1,
        marginBottom: 3,
      }}>
        {value}
      </div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 500 }}>
        {label}
      </div>
    </div>
  );
}

// ============== ADDITION COLUMN COMPONENT ==============
function AdditionColumn({
  additions, onAdditionClick, onAddAddition,
}: {
  additions: DashboardFocusItem[];
  onAdditionClick?: (item: DashboardFocusItem) => void;
  onAddAddition?: () => void;
}) {
  const pending = additions.filter(i => i.status === '待审批' || i.status === '审批中').length;
  const approved = additions.filter(i => i.status === '已批准').length;
  const settled = additions.filter(i => i.status === '已结算').length;
  const rejected = additions.filter(i => i.status === '已驳回').length;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      background: 'var(--bg-control-strong)',
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--border)',
      overflow: 'hidden',
      minWidth: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 14px 10px',
        background: 'var(--bg-control)',
        borderBottom: '1px solid var(--border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 15 }}>{'📊'}</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
            增项动态
          </span>
          <span style={{
            fontSize: 10, fontWeight: 700, fontFamily: '"JetBrains Mono", monospace',
            background: 'var(--accent-gold-soft)', color: 'var(--accent-gold)',
            padding: '1px 6px', borderRadius: 4,
          }}>
            {additions.length}
          </span>
        </div>
        <button
          onClick={onAddAddition}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '4px 10px', background: 'var(--accent-gold)',
            border: 'none', borderRadius: 999,
            fontSize: 11, fontWeight: 600, color: '#ffffff',
            cursor: 'pointer', transition: 'all 0.15s',
            fontFamily: '"Noto Sans SC", sans-serif',
          }}
        >
          + 新增增项
        </button>
      </div>

      {/* Summary at top */}
      <div style={{
        padding: '10px 12px',
        background: 'var(--bg-card)',
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{
          fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
          letterSpacing: '0.08em', textTransform: 'uppercase',
          marginBottom: 8,
        }}>
          {'📈'} 增项汇总
        </div>
        <div style={{ display: 'flex', gap: 5 }}>
          <StatCard label="待审批" value={`${pending}`} color={pending > 0 ? 'var(--accent-amber)' : undefined} />
          <StatCard label="已批准" value={`${approved}`} color={approved > 0 ? 'var(--accent-mint)' : undefined} />
          <StatCard label="已结算" value={`${settled}`} />
        </div>
        {rejected > 0 && (
          <div style={{
            marginTop: 5, textAlign: 'center',
            fontSize: 10, fontWeight: 600,
            color: 'var(--accent-red)',
            padding: '2px 6px', background: 'var(--accent-red-soft)',
            borderRadius: 4, border: '1px solid rgba(196,109,95,0.2)',
          }}>
            已驳回 {rejected} 项
          </div>
        )}
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
        {additions.length > 0 ? additions.map(item => (
          <AdditionStreamItem key={item.id} item={item} onClick={onAdditionClick} />
        )) : (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '40px 16px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 36, marginBottom: 10, opacity: 0.4 }}>{'📦'}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>暂无增项记录</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============== RECTIFICATION COLUMN COMPONENT ==============
function RectificationColumn({
  rectifications, onRectificationClick, onAddRectification,
}: {
  rectifications: DashboardFocusItem[];
  onRectificationClick?: (item: DashboardFocusItem) => void;
  onAddRectification?: () => void;
}) {
  const pending = rectifications.filter(i => i.status === '待处理').length;
  const inProgress = rectifications.filter(i => i.status === '进行中').length;
  const completed = rectifications.filter(i => i.status === '已验收').length;
  const urgent = rectifications.filter(i => i.status === '紧急' || i.status === '高风险').length;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      background: 'var(--bg-control-strong)',
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--border)',
      overflow: 'hidden',
      minWidth: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 14px 10px',
        background: 'var(--bg-control)',
        borderBottom: '1px solid var(--border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 15 }}>{'🔧'}</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
            整改动态
          </span>
          <span style={{
            fontSize: 10, fontWeight: 700, fontFamily: '"JetBrains Mono", monospace',
            background: urgent > 0 ? 'var(--accent-red-soft)' : 'var(--accent-gold-soft)',
            color: urgent > 0 ? 'var(--accent-red)' : 'var(--accent-gold)',
            padding: '1px 6px', borderRadius: 4,
          }}>
            {rectifications.length}
          </span>
        </div>
        <button
          onClick={onAddRectification}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '4px 10px', background: 'var(--accent-blue)',
            border: 'none', borderRadius: 999,
            fontSize: 11, fontWeight: 600, color: '#ffffff',
            cursor: 'pointer', transition: 'all 0.15s',
            fontFamily: '"Noto Sans SC", sans-serif',
          }}
        >
          + 新增整改
        </button>
      </div>

      {/* Summary at top */}
      <div style={{
        padding: '10px 12px',
        background: 'var(--bg-card)',
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{
          fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
          letterSpacing: '0.08em', textTransform: 'uppercase',
          marginBottom: 8,
        }}>
          {'📈'} 整改汇总
        </div>
        <div style={{ display: 'flex', gap: 5 }}>
          <StatCard label="待处理" value={`${pending}`} color={pending > 0 ? 'var(--accent-red)' : undefined} />
          <StatCard label="进行中" value={`${inProgress}`} color={inProgress > 0 ? 'var(--accent-amber)' : undefined} />
          <StatCard label="已验收" value={`${completed}`} color={completed > 0 ? 'var(--accent-mint)' : undefined} />
        </div>
        {urgent > 0 && (
          <div style={{
            marginTop: 5, textAlign: 'center',
            fontSize: 10, fontWeight: 600,
            color: 'var(--accent-red)',
            padding: '2px 6px', background: 'var(--accent-red-soft)',
            borderRadius: 4, border: '1px solid rgba(196,109,95,0.2)',
          }}>
            {'\u26A0\uFE0F'} 紧急/高风险 {urgent} 项
          </div>
        )}
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
        {rectifications.length > 0 ? rectifications.map(item => (
          <RectificationStreamItem key={item.id} item={item} onClick={onRectificationClick} />
        )) : (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '40px 16px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 36, marginBottom: 10, opacity: 0.4 }}>{'\u2705'}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>暂无整改记录</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============== MAIN COMPONENT ==============
const DashboardTower: React.FC<DashboardTowerProps> = ({
  projects = [],
  additions = [],
  rectifications = [],
  loading = false,
  onProjectClick,
  onAdditionClick,
  onRectificationClick,
  onAddProject,
  onAddAddition,
  onAddRectification,
  onExport,
  onRefresh,
  summaryMetrics,
}) => {
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [searchInput, setSearchInput] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('tower');

  const filteredProjects = useMemo(() => {
    let filtered = [...projects];
    if (filterStatus === 'active') filtered = filtered.filter(p => p.status === '进行中');
    else if (filterStatus === 'completed') filtered = filtered.filter(p => p.status === '已完成');
    else if (filterStatus === 'overdue') filtered = filtered.filter(p => toFinite(p.daysRemaining) < 0);
    else if (filterStatus === 'risky') filtered = filtered.filter(p => p.riskTags?.length > 0);
    if (searchInput.trim()) {
      const kw = searchInput.toLowerCase();
      filtered = filtered.filter(p => p.name.toLowerCase().includes(kw) || (p as any).projectCode?.toLowerCase().includes(kw));
    }
    return filtered;
  }, [projects, filterStatus, searchInput]);

  const counts = useMemo(() => ({
    all: projects.length,
    active: projects.filter(p => p.status === '进行中').length,
    completed: projects.filter(p => p.status === '已完成').length,
    overdue: projects.filter(p => toFinite(p.daysRemaining) < 0).length,
    risky: projects.filter(p => p.riskTags?.length > 0).length,
  }), [projects]);

  const metrics = useMemo(() => [
    { key: 'budget', label: '总预算盘面', value: fmt(summaryMetrics?.totalBudget, true), detail: `${summaryMetrics?.totalProjects || 0} 个项目 \xb7 整体可控`, icon: '💰', bg: 'var(--accent-gold-soft)' },
    { key: 'cost', label: '累计统计成本', value: fmt(summaryMetrics?.totalActualCost, true), detail: `${summaryMetrics?.activeProjects || 0} 个项目进行中`, icon: '📈', bg: 'var(--accent-mint-soft)', color: undefined },
    { key: 'usage', label: '整体预算使用率', value: `${(summaryMetrics?.totalBudgetUsage ?? 0).toFixed(1)}%`, detail: (summaryMetrics?.overBudgetCount ?? 0) > 0 ? `${summaryMetrics?.overBudgetCount} 个项目超预算` : '整体预算可控', icon: '📊', bg: 'var(--accent-blue-soft)', color: 'var(--accent-gold)' },
    { key: 'overdue', label: '超期项目', value: `${summaryMetrics?.overdueCount ?? 0}`, detail: '需重点关注', icon: '\u23F0', bg: 'var(--accent-amber-soft)', color: (summaryMetrics?.overdueCount ?? 0) > 0 ? 'var(--accent-amber)' : undefined },
    { key: 'approval', label: '待审批事项', value: `${summaryMetrics?.pendingApprovalCount ?? 0}`, detail: '合同 \xb7 付款 \xb7 变更', icon: '📋', bg: 'var(--accent-purple-soft)', color: 'var(--accent-purple)' },
    { key: 'risky', label: '高风险项目', value: `${summaryMetrics?.riskyCount ?? 0}`, detail: '预算 / 进度 / 质量', icon: '\u26A0\uFE0F', bg: 'var(--accent-red-soft)', color: (summaryMetrics?.riskyCount ?? 0) > 0 ? 'var(--accent-red)' : undefined },
  ], [summaryMetrics]);

  const VIEW_MODES: { key: ViewMode; icon: string; label: string }[] = [
    { key: 'tower', icon: '📈', label: '指挥塔' },
    { key: 'compact', icon: '▢', label: '紧凑' },
    { key: 'table', icon: '\u2630', label: '表格' },
    { key: 'list', icon: '\u2261', label: '列表' },
  ];

  const FILTERS: { key: FilterStatus; label: string }[] = [
    { key: 'all', label: '全部' },
    { key: 'active', label: '进行中' },
    { key: 'completed', label: '已完成' },
    { key: 'overdue', label: '超期' },
    { key: 'risky', label: '\u26A0\uFE0F 高风险' },
  ];

  return (
    <div style={{
      ...tokens,
      fontFamily: '"Noto Sans SC", "Microsoft YaHei", -apple-system, sans-serif',
      background: 'transparent',
      color: 'var(--text-primary)',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&family=Noto+Sans+SC:wght@400;500;700&display=swap');
        * { box-sizing: border-box; }
        @keyframes dtFadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .dt-anim-1 { animation: dtFadeInUp 0.4s ease-out 0.05s both; }
        .dt-anim-2 { animation: dtFadeInUp 0.4s ease-out 0.10s both; }
        .dt-anim-3 { animation: dtFadeInUp 0.4s ease-out 0.15s both; }
        .dt-anim-4 { animation: dtFadeInUp 0.4s ease-out 0.20s both; }
        .dt-anim-5 { animation: dtFadeInUp 0.4s ease-out 0.25s both; }
        .dt-anim-6 { animation: dtFadeInUp 0.4s ease-out 0.30s both; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-thumb { background: var(--bg-surface); border-radius: 3px; }
        ::-webkit-scrollbar-track { background: transparent; }
      `}</style>

      {/* ===== TOPBAR ===== */}
      <div style={{
        padding: '16px 24px', borderBottom: '1px solid var(--border)',
        background: 'var(--bg-control)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
            项目经营总览
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {'🕐'} 最后更新：{new Date().toLocaleString('zh-CN')} \xb7 {projects.length} 个项目
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {([
            { label: '🔄 刷新', onClick: onRefresh },
            { label: '📥 导出', onClick: onExport },
            { label: '\u2795 新建项目', onClick: onAddProject, primary: true },
          ] as any[]).map((btn, i) => (
            <button
              key={i}
              onClick={btn.onClick}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 15px',
                borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.15s',
                fontFamily: '"Noto Sans SC", sans-serif',
                ...(btn.primary ? {
                  background: 'var(--accent-gold)', border: 'none', color: '#ffffff',
                  boxShadow: '0 12px 24px rgba(185, 146, 88, 0.24)',
                } : {
                  background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-secondary)',
                  boxShadow: '0 8px 18px rgba(46, 60, 70, 0.1)',
                }),
              }}
            >
              {btn.label}
            </button>
          ))}
        </div>
      </div>

      {/* ===== INSIGHT STRIP ===== */}
      <div style={{
        padding: '14px 24px', borderBottom: '1px solid var(--border)',
        background: 'var(--bg-control)',
        display: 'flex', gap: 10, overflowX: 'auto',
      }}>
        {metrics.map((m, i) => (
          <div key={m.key} className={`dt-anim-${Math.min(i + 1, 6)}`}>
            <HeroMetricCard metric={m} />
          </div>
        ))}
      </div>

      {/* ===== TOOLBAR ===== */}
      <div style={{
        padding: '12px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-control-strong)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilterStatus(f.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '5px 13px', borderRadius: 999, fontSize: 12, fontWeight: 500,
                border: `1px solid ${filterStatus === f.key ? 'rgba(185, 146, 88, 0.4)' : 'var(--border)'}`,
                background: filterStatus === f.key ? 'var(--accent-gold-soft)' : 'var(--bg-card)',
                color: filterStatus === f.key ? 'var(--accent-gold)' : 'var(--text-secondary)',
                cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
              }}
            >
              {f.label}
              <span style={{
                fontFamily: '"JetBrains Mono", monospace', fontSize: 10, fontWeight: 700,
                background: filterStatus === f.key ? 'rgba(185, 146, 88, 0.2)' : 'var(--bg-surface)',
                padding: '1px 5px', borderRadius: 4,
              }}>
                {counts[f.key]}
              </span>
            </button>
          ))}

          <div style={{
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '5px 12px', background: 'var(--bg-card)',
            border: '1px solid rgba(84, 117, 144, 0.18)', borderRadius: 'var(--radius-sm)',
          }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>🔍</span>
            <input
              type="text" placeholder="搜索项目名称或编码..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              style={{
                background: 'none', border: 'none', outline: 'none',
                fontSize: 13, color: 'var(--text-primary)',
                fontFamily: '"Noto Sans SC", sans-serif', width: 200,
              }}
            />
          </div>
        </div>

        <div style={{
          display: 'flex', background: 'var(--bg-card)',
          border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden',
        }}>
          {VIEW_MODES.map(vm => (
            <button
              key={vm.key}
              onClick={() => setViewMode(vm.key)}
              style={{
                padding: '5px 12px', border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: viewMode === vm.key ? 600 : 400,
                color: viewMode === vm.key ? '#ffffff' : 'var(--text-muted)',
                background: viewMode === vm.key ? 'var(--accent-gold)' : 'transparent',
                transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              {vm.icon}
            </button>
          ))}
        </div>
      </div>

      {/* ===== THREE-COLUMN CONTENT ===== */}
      <div style={{
        display: 'flex',
        height: 'calc(100vh - 300px)',
        minHeight: 400,
        overflow: 'hidden',
      }}>
        {/* ===== LEFT: PROJECT GRID ===== */}
        <div style={{
          flex: 1, minWidth: 0,
          overflowY: 'auto',
          padding: '16px 16px 16px 24px',
        }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 80, color: 'var(--text-muted)' }}>
              加载中...
            </div>
          ) : filteredProjects.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80, color: 'var(--text-muted)' }}>
              <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.5 }}>{'📦'}</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
                {searchInput ? '未找到匹配的项目' : '暂无项目数据'}
              </div>
              <div style={{ fontSize: 13 }}>
                {searchInput ? '尝试调整搜索关键词' : '点击右上角"新建项目"开始添加'}
              </div>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: 14,
              alignContent: 'start',
            }}>
              {filteredProjects.map((project, i) => (
                <div key={project.id} className={`dt-anim-${Math.min(i % 6 + 1, 6)}`}>
                  <ProjectCard project={project} onClick={onProjectClick} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ===== MIDDLE: ADDITION COLUMN ===== */}
        <div style={{
          width: 300, flexShrink: 0,
          padding: '16px 8px',
          overflowY: 'auto',
        }}>
          <AdditionColumn
            additions={additions}
            onAdditionClick={onAdditionClick}
            onAddAddition={onAddAddition}
          />
        </div>

        {/* ===== RIGHT: RECTIFICATION COLUMN ===== */}
        <div style={{
          width: 300, flexShrink: 0,
          padding: '16px 24px 16px 8px',
          overflowY: 'auto',
        }}>
          <RectificationColumn
            rectifications={rectifications}
            onRectificationClick={onRectificationClick}
            onAddRectification={onAddRectification}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardTower;
